import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import {
  ActionFormOverlay,
  ActionFormShell,
  ActionFormHeader,
  ActionFormBody,
  ActionFormFooter,
} from '../../shared/ActionFormShell';
import ProvidentFundRatesSection from './sections/ProvidentFundRatesSection.jsx';
import StatutoryActHistoryList, {
  ActTabBar,
  TaxRegimeSidebar,
  STATUTORY_ACT_TABS,
} from './StatutoryActHistoryList.jsx';
import PolicyListToolbar from '../../policy-wizard/PolicyListToolbar.jsx';
import InfoHint from '../../attendance/components/holiday/InfoHint.jsx';
import DatePicker from '../../../Common/DatePicker';
import { formatDate } from '../../../../lib/employeeHelpers';
import { salaryStructureService } from '../../../../services/api';
import {
  loadStatutoryRatesFromStorage,
  saveStatutoryRatesToStorage,
  buildStatutoryConfigPayload,
  applyStatutoryConfigJson,
  getDefaultIncomeTaxRates,
  STATUTORY_BASELINE_DATE,
} from './utils/statutoryRatesDefaults';
import { normalizePtStateConfig } from './utils/professionalTaxEngine';
import { DsButton, DsInput } from '../../../../design-system';
import { Trash2 } from 'lucide-react';
import {
  resolveCompanyMinDate,
  maxDate,
  toYmd,
} from '../../../../utils/selectableDateBounds';

function getPtConfigForState(rates, stateName) {
  const raw = rates?.pt?.states?.[stateName];
  // Always return rich config; supports legacy slab arrays still in localStorage/API.
  return normalizePtStateConfig(raw, stateName || rates?.pt?.currentState || "");
}

/** Newest-first by date (YYYY-MM-DD). */
function sortHistoryDesc(entries = []) {
  return [...(entries || [])].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
}

function historyEntryKey(entry) {
  return `${String(entry?.date || "").slice(0, 10)}::${entry?.id ?? ""}`;
}

function getPtSlabsForUi(rates, stateName) {
  const cfg = getPtConfigForState(rates, stateName);
  return Array.isArray(cfg.slabs) ? cfg.slabs : [];
}

function withPtStateUpdate(rates, stateName, updater) {
  const current = getPtConfigForState(rates, stateName);
  const next = typeof updater === 'function' ? updater(current) : updater;
  return {
    ...rates,
    pt: {
      ...rates.pt,
      states: {
        ...rates.pt.states,
        [stateName]: normalizePtStateConfig(next, stateName),
      },
    },
  };
}

/** Act → persisted history section key. */
const ACT_TITLES = {
  pf: 'Provident Fund (PF)',
  esi: 'Employee State Insurance (ESI)',
  pt: 'Professional Tax (PT)',
  gratuity: 'Gratuity',
  tax: 'Tax Rates',
};

const ACT_INFO = {
  pf: 'Saved PF revisions are listed by effective date. Use + New rate to start a revision from the current values, edit the fields below and Save. The 3-dot menu can load a past revision into the editor (Edit), switch it off (Deactivate / Reactivate) or remove the newest revision (Delete). The oldest baseline revision cannot be deactivated or deleted. Payroll uses the latest active revision on or before the pay month.',
  esi: 'Saved ESI revisions are listed by effective date. Use + New rate to start a revision, edit the fields below and Save. Edit loads a past revision into the editor; Deactivate hides it from payroll without losing it; Delete removes only the newest revision. The oldest baseline revision is protected.',
  pt: 'Professional Tax is saved per state; payroll picks the slabs of the employee\'s Branch → State. The list shows revisions of the state selected in the editor. Use + New rate to start a revision, Edit to load a past one, Deactivate / Reactivate to include or exclude it from payroll, and Delete to remove the newest revision. The oldest baseline revision is protected.',
  gratuity: 'Gratuity = Basic wages ÷ 26 × 15 × completed years of service. Minimum Service years (Full-time / Contract) decide eligibility in Full & Final settlement; default is 5. Use + New rate to start a revision, Edit to load a past one, Deactivate / Reactivate to include or exclude it, Delete for the newest only. The oldest baseline revision is protected.',
  tax: 'Old and New regime slabs, cess, standard deduction and surcharge are versioned separately by effective date. Use + New rate to start a revision, Edit to load a past one, Deactivate / Reactivate to include or exclude it from TDS, Delete for the newest only. The oldest baseline revision is protected.',
};

const reviveSlabs = (list) =>
  (Array.isArray(list) ? list : []).map((s) => ({
    ...s,
    min: Number(s.min) || 0,
    max:
      s.max == null || s.max === '' || s.max === Infinity || String(s.max).toLowerCase() === 'infinity'
        ? Infinity
        : Number(s.max),
    rate: Number(s.rate) || 0,
  }));

const StatutoryRatesManager = ({
  focusedAct = null,
  taxRegime = null,
  onTaxRegimeChange = null,
}) => {
  // Legacy mounts (no focusedAct) get the same act tabs internally.
  const [internalAct, setInternalAct] = useState('pf');
  const [internalRegime, setInternalRegime] = useState('old');
  const [listSearch, setListSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  // null | 'new' | 'edit' — New/Edit rate opens the editor in a modal; snapshot lets Cancel discard drafts.
  const [rateForm, setRateForm] = useState(null);
  const rateSnapRef = useRef(null);
  const activeAct = focusedAct || internalAct;
  const regime = taxRegime ?? internalRegime;
  const setRegime = (r) => {
    setInternalRegime(r);
    onTaxRegimeChange?.(r);
  };
  // Parent (StatutoryRatesHeadsPanel) owns the sidebar when it passes a regime/handler.
  const showInternalSidebar = activeAct === 'tax' && !onTaxRegimeChange && taxRegime == null;
  const actSection =
    activeAct === 'tax' ? (regime === 'new' ? 'incomeTaxNew' : 'incomeTaxOld') : activeAct;
  const statutoryMinDate = useMemo(() => {
    return (
      toYmd(maxDate(STATUTORY_BASELINE_DATE, resolveCompanyMinDate())) ||
      STATUTORY_BASELINE_DATE
    );
  }, []);

  const [configId, setConfigId] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedSectionState, setSelectedSection] = useState('');
  const [ptHistoryStateState, setPtHistoryState] = useState('Karnataka');
  const selectedSection = selectedSectionState;
  const ptHistoryState = ptHistoryStateState;
  const [showAddStateModal, setShowAddStateModal] = useState(false);
  const [newStateName, setNewStateName] = useState('');
  const [effectiveDates, setEffectiveDates] = useState({
    pf: '2026-04-01',
    esi: '2026-04-01',
    pt: '2026-04-01',
    gratuity: '2026-04-01',
    incomeTax: '2026-04-01',
    incomeTaxOld: '2026-04-01',
    incomeTaxNew: '2026-04-01'
  });
  const [history, setHistory] = useState({
    pf: [
      { id: 1, date: '2024-01-01', changes: 'Initial PF rates setup', rates: { employeeShare: 12, employerShare: 12, pfWageCeiling: 15000, epsWageCeiling: 15000, edliWageCeiling: 15000, ceiling: 15000, epf: 3.67, eps: 8.33, adminCharges: 0.5, edliRate: 0.5 } }
    ],
    esi: [
      { id: 1, date: '2024-01-01', changes: 'Initial ESI rates setup', rates: { employeeShare: 0.75, employerShare: 3.25, ceiling: 21000, cutoff: 21000 } }
    ],
    pt: {
      'Karnataka': [
        { id: 1, date: '2024-01-01', changes: 'Initial Karnataka PT rates setup', rates: { slabs: [{ min: 0, max: 15000, rate: 200 }, { min: 15001, max: 30000, rate: 250 }] } }
      ],
      'Maharashtra': [
        { id: 1, date: '2024-01-01', changes: 'Initial Maharashtra PT rates setup', rates: { slabs: [{ min: 0, max: 5000, rate: 0 }, { min: 5001, max: 10000, rate: 175 }] } }
      ],
      'Tamil Nadu': [
        { id: 1, date: '2024-01-01', changes: 'Initial Tamil Nadu PT rates setup', rates: { slabs: [{ min: 0, max: 21000, rate: 0 }, { min: 21001, max: Infinity, rate: 2500 }] } }
      ],
      'West Bengal': [
        { id: 1, date: '2024-01-01', changes: 'Initial West Bengal PT rates setup', rates: { slabs: [{ min: 0, max: 10000, rate: 110 }, { min: 10001, max: 15000, rate: 130 }] } }
      ],
      'Gujarat': [
        { id: 1, date: '2024-01-01', changes: 'Initial Gujarat PT rates setup', rates: { slabs: [{ min: 0, max: 5000, rate: 0 }, { min: 5001, max: 10000, rate: 200 }] } }
      ],
      'Delhi': [
        { id: 1, date: '2024-01-01', changes: 'Initial Delhi PT rates setup', rates: { slabs: [{ min: 0, max: 10000, rate: 0 }, { min: 10001, max: 20000, rate: 200 }] } }
      ]
    },
    gratuity: [
      { id: 1, date: '2024-01-01', changes: 'Initial Gratuity rates setup', rates: { rate: 4.81 } }
    ],
    incomeTax: [
      { id: 1, date: '2024-01-01', changes: 'Initial Income Tax rates setup', rates: { old: [{ min: 0, max: 250000, rate: 0 }], new: [{ min: 0, max: 300000, rate: 0 }], cess: 4 } }
    ],
    incomeTaxOld: [
      { id: 1, date: '2024-01-01', changes: 'Initial Old Regime Tax rates setup', rates: { cess: 4, slabs: [{ min: 0, max: 250000, rate: 0 }], surcharge: [{ min: 0, max: 5000000, rate: 0 }] } }
    ],
    incomeTaxNew: [
      { id: 1, date: '2024-01-01', changes: 'Initial New Regime Tax rates setup', rates: { cess: 4, slabs: [{ min: 0, max: 300000, rate: 0 }], surcharge: [{ min: 0, max: 5000000, rate: 0 }] } }
    ]
  });
  const [rates, setRates] = useState(() => {
    // Force-normalize PT states (object config, not legacy slab arrays) on first paint.
    const loaded = loadStatutoryRatesFromStorage();
    return {
      ...loaded,
      pt: {
        ...loaded.pt,
        currentState: loaded.pt?.currentState || 'Karnataka',
        states: Object.fromEntries(
          Object.entries(loaded.pt?.states || {}).map(([name, cfg]) => [
            name,
            normalizePtStateConfig(cfg, name),
          ])
        ),
      },
      incomeTax: getDefaultIncomeTaxRates(),
    };
  });

  const persistRatesForSimulator = (nextRates) => {
    saveStatutoryRatesToStorage({
      pf: nextRates.pf,
      esi: nextRates.esi,
      pt: nextRates.pt,
      gratuity: nextRates.gratuity,
    });
  };

  const applyPublishedConfig = useCallback((configJson) => {
    if (!configJson) return;
    const applied = applyStatutoryConfigJson(
      configJson,
      loadStatutoryRatesFromStorage()
    );
    if (applied.rates) {
      const normalizedRates = {
        ...applied.rates,
        pt: {
          ...applied.rates.pt,
          currentState: applied.rates.pt?.currentState || "Karnataka",
          states: Object.fromEntries(
            Object.entries(applied.rates.pt?.states || {}).map(([name, cfg]) => [
              name,
              normalizePtStateConfig(cfg, name),
            ])
          ),
        },
      };
      setRates(normalizedRates);
      persistRatesForSimulator(normalizedRates);
    }
    if (applied.history) setHistory(applied.history);
    if (applied.effectiveDates) setEffectiveDates(applied.effectiveDates);
  }, []);

  const persistToServer = useCallback(
    async (nextRates, nextHistory, nextEffectiveDates, forcedConfigId = null) => {
      let id = forcedConfigId || configId;
      if (!id) {
        try {
          const res = await salaryStructureService.getStatutoryConfig();
          id = res?.data?.id;
          if (id) setConfigId(id);
        } catch (err) {
          console.error('Failed to load statutory config id', err);
        }
      }
      if (!id) {
        toast.error('Could not connect to statutory rates API. Ensure backend is running and migrations are applied.');
        return null;
      }
      setSavingConfig(true);
      try {
        const configJson = buildStatutoryConfigPayload(
          nextRates,
          nextHistory,
          nextEffectiveDates
        );
        const res = await salaryStructureService.updateStatutoryConfig(id, { configJson });
        const published = res?.data?.configJson || res?.data?.config_json || configJson;
        // Simulator uses published live rates (≤ today), not future-dated editor values
        persistRatesForSimulator(published.rates || nextRates);
        return published;
      } catch (err) {
        console.error('Failed to save statutory rates', err);
        toast.error(err?.response?.data?.message || 'Failed to save statutory rates to server');
        return null;
      } finally {
        setSavingConfig(false);
      }
    },
    [configId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingConfig(true);
      try {
        const res = await salaryStructureService.getStatutoryConfig();
        if (cancelled) return;
        const row = res?.data;
        if (!row?.id) return;
        setConfigId(row.id);
        const applied = applyStatutoryConfigJson(
          row.configJson || row.config_json,
          loadStatutoryRatesFromStorage()
        );
        if (applied.rates) {
          const normalizedRates = {
            ...applied.rates,
            pt: {
              ...applied.rates.pt,
              currentState: applied.rates.pt?.currentState || 'Karnataka',
              states: Object.fromEntries(
                Object.entries(applied.rates.pt?.states || {}).map(([name, cfg]) => [
                  name,
                  normalizePtStateConfig(cfg, name),
                ])
              ),
            },
          };
          setRates(normalizedRates);
          persistRatesForSimulator(normalizedRates);
        }
        if (applied.history) setHistory(applied.history);
        if (applied.effectiveDates) setEffectiveDates(applied.effectiveDates);
      } catch (err) {
        console.error('Failed to load statutory rates from API', err);
        const serverMsg = err?.response?.data?.message;
        toast.error(
          serverMsg
            ? `Could not load statutory rates: ${serverMsg}`
            : 'Using local statutory rates — could not load from server'
        );
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRateChange = (category, field, value) => {
    setRates(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: typeof value === 'boolean' ? value : parseFloat(value) || 0
      }
    }));
  };

  const handleEffectiveDateChange = (section, date) => {
    setEffectiveDates(prev => ({
      ...prev,
      [section]: date
    }));
  };

  const handleSaveSection = async (section) => {
    const effectiveDate = effectiveDates[section];
    if (!effectiveDate) {
      alert('Please select an Effective Date');
      return false;
    }
    if (effectiveDate < statutoryMinDate) {
      alert(
        `Effective date cannot be before ${statutoryMinDate} (service commencement / statutory baseline).`
      );
      return false;
    }

    const today = new Date().toISOString().slice(0, 10);
    if (effectiveDate > today) {
      const ok = window.confirm(
        `This revision is dated ${effectiveDate} (in the future).\n\n` +
          `It will be stored in History and will only apply to salary structure / payroll / TDS ` +
          `for months on or after that date. Live rates used today will not change until then.\n\nContinue?`
      );
      if (!ok) return false;
    }

    let nextHistory = history;

    if (section === 'pt') {
      const currentState = rates.pt.currentState;
      const stateHistory = history.pt[currentState] || [];
      const existingEntry = stateHistory.find(entry => entry.date === effectiveDate);
      if (existingEntry) {
        const confirmUpdate = window.confirm(
          `An entry already exists for ${currentState} on ${effectiveDate}. This will update the existing entry. Continue?`
        );
        if (!confirmUpdate) return false;
      }

      const newEntry = {
        id: Date.now(),
        date: effectiveDate,
        changes: `Updated ${currentState} PT rates`,
        active: true,
        rates: { ...getPtConfigForState(rates, currentState) }
      };

      const updatedHistory = existingEntry 
        ? stateHistory.map(entry => entry.date === effectiveDate ? newEntry : entry)
        : [...stateHistory, newEntry].sort((a, b) => new Date(b.date) - new Date(a.date));

      nextHistory = {
        ...history,
        pt: {
          ...history.pt,
          [currentState]: updatedHistory
        }
      };
      setHistory(nextHistory);

      const saved = await persistToServer(rates, nextHistory, effectiveDates);
      if (saved) {
        toast.success(`${currentState} PT rates saved successfully!`);
      }
      return !!saved;
    } else {
      const existingEntry = history[section].find(entry => entry.date === effectiveDate);
      if (existingEntry) {
        const confirmUpdate = window.confirm(
          `An entry already exists for ${effectiveDate}. This will update the existing entry. Continue?`
        );
        if (!confirmUpdate) return false;
      }

      const newEntry = {
        id: Date.now(),
        date: effectiveDate,
        changes: `Updated ${section.toUpperCase()} rates`,
        active: true,
        rates:
          section === "incomeTaxOld"
            ? {
                cess: rates.incomeTax.cess,
                slabs: [...(rates.incomeTax.slabs?.old || [])],
                surcharge: [...(rates.incomeTax.surcharge?.old || [])],
                standardDeduction: Number(rates.incomeTax.standardDeduction?.old) || 50000,
              }
            : section === "incomeTaxNew"
              ? {
                  cess: rates.incomeTax.cess,
                  slabs: [...(rates.incomeTax.slabs?.new || [])],
                  surcharge: [...(rates.incomeTax.surcharge?.new || [])],
                  standardDeduction: Number(rates.incomeTax.standardDeduction?.new) || 75000,
                }
              : { ...(rates[section] || {}) },
      };

      const updatedHistory = existingEntry 
        ? history[section].map(entry => entry.date === effectiveDate ? newEntry : entry)
        : [...history[section], newEntry].sort((a, b) => new Date(b.date) - new Date(a.date));

      nextHistory = {
        ...history,
        [section]: updatedHistory
      };
      setHistory(nextHistory);

      const saved = await persistToServer(rates, nextHistory, effectiveDates);
      if (saved) {
        toast.success(`${section.toUpperCase()} rates saved successfully!`);
      }
      return !!saved;
    }
  };

  const handleViewHistory = (section) => {
    setSelectedSection(section);
    setShowHistoryModal(true);
  };

  /**
   * Delete only the newest history entry (LIFO), like salary revision delete.
   * Live rates republish from remaining history ≤ today. Does not re-process payroll.
   */
  const handleDeleteHistoryEntry = async (entry, opts = {}) => {
    // opts lets the saved-list menu reuse this without opening the History modal.
    const selectedSection = opts.section || selectedSectionState;
    const ptHistoryState = opts.ptState || ptHistoryStateState;
    if (!entry?.date || !selectedSection) return;

    const isPt = selectedSection === "pt";
    const list = isPt
      ? history.pt?.[ptHistoryState] || []
      : history[selectedSection] || [];
    const sorted = sortHistoryDesc(list);

    if (sorted.length <= 1) {
      toast.error(
        "Cannot delete the only history entry. Save a new revision to change rates instead."
      );
      return;
    }

    const latest = sorted[0];
    const entryDate = String(entry.date).slice(0, 10);
    const latestDate = String(latest.date).slice(0, 10);
    if (entryDate !== latestDate) {
      toast.error(
        "Only the latest history entry can be deleted. Remove newest first."
      );
      return;
    }

    const label = isPt
      ? `${ptHistoryState} PT`
      : String(selectedSection).toUpperCase();
    const ok = window.confirm(
      `Delete the latest ${label} history entry dated ${formatDate(entry.date)}?\n\n` +
        `Live rates will revert to the previous history update. This cannot be undone.\n\n` +
        `Already processed payroll months are not changed automatically — re-process only if needed.`
    );
    if (!ok) return;

    const remaining = sortHistoryDesc(
      list.filter((e) => String(e.date).slice(0, 10) !== entryDate)
    );
    const previous = remaining[0] || null;

    let nextHistory;
    if (isPt) {
      nextHistory = {
        ...history,
        pt: {
          ...history.pt,
          [ptHistoryState]: remaining,
        },
      };
    } else {
      nextHistory = {
        ...history,
        [selectedSection]: remaining,
      };
    }

    const nextEffectiveDates = {
      ...effectiveDates,
      [selectedSection]: previous?.date || effectiveDates[selectedSection],
    };

    // Seed editor rates from previous snapshot so payload stays coherent before publish.
    let nextRates = rates;
    if (previous?.rates) {
      if (isPt) {
        nextRates = {
          ...rates,
          pt: {
            ...rates.pt,
            states: {
              ...rates.pt.states,
              [ptHistoryState]: normalizePtStateConfig(
                previous.rates,
                ptHistoryState
              ),
            },
          },
        };
      } else if (
        selectedSection === "incomeTaxOld" ||
        selectedSection === "incomeTaxNew"
      ) {
        // Live income tax is merged on publish; keep current editor until server returns.
        nextRates = rates;
      } else if (rates[selectedSection]) {
        nextRates = {
          ...rates,
          [selectedSection]: {
            ...rates[selectedSection],
            ...previous.rates,
          },
        };
      }
    }

    const published = await persistToServer(
      nextRates,
      nextHistory,
      nextEffectiveDates
    );
    if (!published) return;

    applyPublishedConfig(published);
    toast.success(
      `${label} history deleted. Live rates restored to previous update.`
    );
  };

  /* ---------- Saved-list actions (reuse the same persist/publish paths as Save/Delete) ---------- */
  const getSectionEntries = (section) =>
    section === 'pt' ? history.pt?.[rates.pt.currentState] || [] : history[section] || [];

  const openRateForm = (mode) => {
    rateSnapRef.current = { rates, effectiveDates };
    setRateForm(mode);
  };

  /** Cancel / Esc / backdrop: drop unsaved drafts (unless a PT state was added meanwhile). */
  const closeRateForm = useCallback(() => {
    const snap = rateSnapRef.current;
    if (snap) {
      setEffectiveDates(snap.effectiveDates);
      setRates((prev) =>
        Object.keys(prev.pt.states).length === Object.keys(snap.rates.pt.states).length
          ? snap.rates
          : prev
      );
    }
    rateSnapRef.current = null;
    setRateForm(null);
  }, []);

  useEffect(() => {
    if (!rateForm) return undefined;
    const onKey = (e) => e.key === 'Escape' && closeRateForm();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [rateForm, closeRateForm]);

  /** + New rate: start from current rates, default the date to today (or baseline), open the modal. */
  const handleNewRate = (section) => {
    const today = new Date().toISOString().slice(0, 10);
    openRateForm('new');
    handleEffectiveDateChange(section, today > statutoryMinDate ? today : statutoryMinDate);
  };

  /** Edit: load a saved entry's rates + date into the modal (Save then updates that date). */
  const handleEditEntry = (section, entry) => {
    openRateForm('edit');
    const r = entry?.rates || {};
    if (section === 'pt') {
      setRates((prev) =>
        withPtStateUpdate(prev, prev.pt.currentState, (cfg) => ({ ...cfg, ...r }))
      );
    } else if (section === 'incomeTaxOld' || section === 'incomeTaxNew') {
      const k = section === 'incomeTaxNew' ? 'new' : 'old';
      setRates((prev) => ({
        ...prev,
        incomeTax: {
          ...prev.incomeTax,
          cess: r.cess ?? prev.incomeTax.cess,
          slabs: {
            ...prev.incomeTax.slabs,
            [k]: Array.isArray(r.slabs) ? reviveSlabs(r.slabs) : prev.incomeTax.slabs[k],
          },
          surcharge: {
            ...prev.incomeTax.surcharge,
            [k]: Array.isArray(r.surcharge) ? reviveSlabs(r.surcharge) : prev.incomeTax.surcharge[k],
          },
          standardDeduction: {
            ...(typeof prev.incomeTax.standardDeduction === 'object'
              ? prev.incomeTax.standardDeduction
              : {}),
            [k]: r.standardDeduction ?? prev.incomeTax.standardDeduction?.[k],
          },
        },
      }));
    } else {
      setRates((prev) => ({ ...prev, [section]: { ...prev[section], ...r } }));
    }
    handleEffectiveDateChange(section, String(entry.date).slice(0, 10));
  };

  /** Deactivate / Reactivate a saved entry and republish live rates. Oldest baseline is protected. */
  const handleToggleEntryActive = async (section, entry) => {
    const list = getSectionEntries(section);
    const sorted = sortHistoryDesc(list);
    const entryDate = String(entry.date).slice(0, 10);
    const nextActive = entry.active === false;

    if (
      !nextActive &&
      (sorted.length <= 1 || entryDate === String(sorted[sorted.length - 1].date).slice(0, 10))
    ) {
      toast.error('The baseline (oldest) entry cannot be deactivated.');
      return;
    }

    const label = section === 'pt' ? `${rates.pt.currentState} PT` : section.toUpperCase();
    const ok = window.confirm(
      nextActive
        ? `Reactivate the ${label} entry dated ${formatDate(entry.date)}?\n\nIt will apply again to months on or after that date.`
        : `Deactivate the ${label} entry dated ${formatDate(entry.date)}?\n\nPayroll will use the previous active entry instead. You can reactivate it later.`
    );
    if (!ok) return;

    const updated = list.map((e) =>
      String(e.date).slice(0, 10) === entryDate ? { ...e, active: nextActive } : e
    );
    const nextHistory =
      section === 'pt'
        ? { ...history, pt: { ...history.pt, [rates.pt.currentState]: updated } }
        : { ...history, [section]: updated };

    const published = await persistToServer(rates, nextHistory, effectiveDates);
    if (!published) return;
    applyPublishedConfig(published);
    toast.success(`${label} entry ${nextActive ? 'reactivated' : 'deactivated'}.`);
  };

  const getHistoryEntriesForModal = () => {
    if (selectedSection === "pt") {
      return sortHistoryDesc(history.pt?.[ptHistoryState] || []);
    }
    return sortHistoryDesc(history[selectedSection] || []);
  };

  const handlePTStateChange = (state) => {
    setRates(prev => ({
      ...prev,
      pt: { ...prev.pt, currentState: state }
    }));
  };

  const handlePTRateChange = (index, field, value) => {
    const currentState = rates.pt.currentState;
    setRates((prev) =>
      withPtStateUpdate(prev, currentState, (cfg) => {
        const newSlabs = [...(cfg.slabs || [])];
        newSlabs[index] = {
          ...newSlabs[index],
          [field]:
            field === 'max' && (value === '' || value === Infinity)
              ? Infinity
              : parseFloat(value) || 0,
        };
        return { ...cfg, slabs: newSlabs };
      })
    );
  };

  const handlePtMetaChange = (field, value) => {
    const currentState = rates.pt.currentState;
    setRates((prev) =>
      withPtStateUpdate(prev, currentState, (cfg) => ({
        ...cfg,
        [field]: value,
      }))
    );
  };

  const MONTH_OPTIONS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const handleAddPtMonthOverride = () => {
    const currentState = rates.pt.currentState;
    setRates((prev) =>
      withPtStateUpdate(prev, currentState, (cfg) => ({
        ...cfg,
        specialRules: [
          ...(cfg.specialRules || []),
          {
            type: 'MONTH_OVERRIDE',
            month: 2,
            gender: '',
            minGross: 0,
            replaceAmount: 300,
          },
        ],
      }))
    );
  };

  const handlePtMonthOverrideChange = (index, field, value) => {
    const currentState = rates.pt.currentState;
    setRates((prev) =>
      withPtStateUpdate(prev, currentState, (cfg) => {
        const rules = [...(cfg.specialRules || [])];
        const row = { ...(rules[index] || { type: 'MONTH_OVERRIDE' }) };
        if (field === 'month' || field === 'minGross' || field === 'replaceAmount') {
          row[field] = value === '' ? '' : Number(value);
        } else if (field === 'gender') {
          row.gender = value || '';
        } else {
          row[field] = value;
        }
        row.type = 'MONTH_OVERRIDE';
        rules[index] = row;
        return { ...cfg, specialRules: rules };
      })
    );
  };

  const handleDeletePtMonthOverride = (index) => {
    const currentState = rates.pt.currentState;
    setRates((prev) =>
      withPtStateUpdate(prev, currentState, (cfg) => ({
        ...cfg,
        specialRules: (cfg.specialRules || []).filter((_, i) => i !== index),
      }))
    );
  };

  const handleIncomeTaxSlabChange = (regime, index, field, value) => {
    const newSlabs = [...rates.incomeTax.slabs[regime]];
    newSlabs[index] = { ...newSlabs[index], [field]: parseFloat(value) || 0 };
    setRates(prev => ({
      ...prev,
      incomeTax: {
        ...prev.incomeTax,
        slabs: {
          ...prev.incomeTax.slabs,
          [regime]: newSlabs
        }
      }
    }));
  };

  const handleAddPTSlab = () => {
    const currentState = rates.pt.currentState;
    setRates((prev) =>
      withPtStateUpdate(prev, currentState, (cfg) => ({
        ...cfg,
        slabs: [...(cfg.slabs || []), { min: 0, max: Infinity, rate: 0 }],
      }))
    );
  };

  const handleDeletePTSlab = (index) => {
    const currentState = rates.pt.currentState;
    const cfg = getPtConfigForState(rates, currentState);
    if ((cfg.slabs || []).length <= 1) return;
    setRates((prev) =>
      withPtStateUpdate(prev, currentState, (c) => ({
        ...c,
        slabs: (c.slabs || []).filter((_, i) => i !== index),
      }))
    );
  };

  const handleAddIncomeTaxSlab = (regime) => {
    const newSlabs = [...rates.incomeTax.slabs[regime], { min: 0, max: Infinity, rate: 0 }];
    setRates(prev => ({
      ...prev,
      incomeTax: {
        ...prev.incomeTax,
        slabs: {
          ...prev.incomeTax.slabs,
          [regime]: newSlabs
        }
      }
    }));
  };

  const handleDeleteIncomeTaxSlab = (regime, index) => {
    if (rates.incomeTax.slabs[regime].length > 1) {
      const newSlabs = rates.incomeTax.slabs[regime].filter((_, i) => i !== index);
      setRates(prev => ({
        ...prev,
        incomeTax: {
          ...prev.incomeTax,
          slabs: {
            ...prev.incomeTax.slabs,
            [regime]: newSlabs
          }
        }
      }));
    }
  };

  const handleSurchargeChange = (regime, index, field, value) => {
    const newSurcharge = [...rates.incomeTax.surcharge[regime]];
    newSurcharge[index] = { ...newSurcharge[index], [field]: parseFloat(value) || 0 };
    setRates(prev => ({
      ...prev,
      incomeTax: {
        ...prev.incomeTax,
        surcharge: {
          ...prev.incomeTax.surcharge,
          [regime]: newSurcharge
        }
      }
    }));
  };

  const handleAddSurchargeSlab = (regime) => {
    const newSurcharge = [...rates.incomeTax.surcharge[regime], { min: 0, max: Infinity, rate: 0 }];
    setRates(prev => ({
      ...prev,
      incomeTax: {
        ...prev.incomeTax,
        surcharge: {
          ...prev.incomeTax.surcharge,
          [regime]: newSurcharge
        }
      }
    }));
  };

  const handleDeleteSurchargeSlab = (regime, index) => {
    if (rates.incomeTax.surcharge[regime].length > 1) {
      const newSurcharge = rates.incomeTax.surcharge[regime].filter((_, i) => i !== index);
      setRates(prev => ({
        ...prev,
        incomeTax: {
          ...prev.incomeTax,
          surcharge: {
            ...prev.incomeTax.surcharge,
            [regime]: newSurcharge
          }
        }
      }));
    }
  };

  const handleAddNewState = () => {
    if (!newStateName.trim()) {
      alert('Please enter a state name');
      return;
    }

    const stateName = newStateName.trim();
    
    // Check if state already exists
    if (rates.pt.states[stateName]) {
      alert('State already exists!');
      return;
    }

    // Add new state with default rich PT config
    const newStateCfg = normalizePtStateConfig({
      applicable: true,
      frequency: 'MONTHLY',
      slabs: [{ min: 0, max: Infinity, rate: 0 }],
      sourceNotes: '',
    }, stateName);

    setRates(prev => ({
      ...prev,
      pt: {
        ...prev.pt,
        states: {
          ...prev.pt.states,
          [stateName]: newStateCfg
        }
      }
    }));

    // Add to history
    setHistory(prev => ({
      ...prev,
      pt: {
        ...prev.pt,
        [stateName]: [
          {
            id: Date.now(),
            date: new Date().toISOString().slice(0, 10),
            changes: `Initial ${stateName} PT rates setup`,
            rates: { ...newStateCfg }
          }
        ]
      }
    }));

    // Switch to new state
    setRates(prev => ({
      ...prev,
      pt: { ...prev.pt, currentState: stateName }
    }));

    setNewStateName('');
    setShowAddStateModal(false);
    alert(`${stateName} state added successfully!`);
  };


  const actTitle =
    activeAct === 'tax'
      ? `Tax Rates — ${regime === 'new' ? 'New' : 'Old'} Regime`
      : ACT_TITLES[activeAct];

  return (
    <div className="space-y-4">
      {loadingConfig && (
        <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading statutory rates…</p>
      )}
      {savingConfig && (
        <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Saving to server…</p>
      )}
      {!focusedAct && (
        <ActTabBar tabs={STATUTORY_ACT_TABS} value={activeAct} onChange={setInternalAct} />
      )}

      <div className={showInternalSidebar ? 'flex items-start gap-5' : ''}>
        {showInternalSidebar && <TaxRegimeSidebar value={regime} onChange={setRegime} />}
        <div className="min-w-0 flex-1 space-y-5">
          <PolicyListToolbar
            title={actTitle}
            newLabel="+ New rate"
            onNew={() => handleNewRate(actSection)}
            searchValue={listSearch}
            onSearchChange={(e) => setListSearch(e.target.value)}
            showInactive={showInactive}
            onToggleInactive={() => setShowInactive((v) => !v)}
            searchPlaceholder="Search saved rates..."
            rolesSlot={
              <InfoHint
                title={`${ACT_INFO[activeAct]}\n\nSet an Effective Date when you Save. Salary Structure, employee breakdown, Process Payroll and TDS use the rates effective for that month; future-dated revisions apply only from that date. Baseline: ${STATUTORY_BASELINE_DATE}. Earliest selectable: ${statutoryMinDate}.`}
              />
            }
          />
          <StatutoryActHistoryList
            section={actSection}
            entries={getSectionEntries(actSection)}
            search={listSearch}
            showInactive={showInactive}
            canEdit
            busy={savingConfig}
            onEdit={(entry) => handleEditEntry(actSection, entry)}
            onToggleActive={(entry) => handleToggleEntryActive(actSection, entry)}
            onDelete={(entry) =>
              handleDeleteHistoryEntry(entry, { section: actSection, ptState: rates.pt.currentState })
            }
          />
          {!rateForm && (
            <p className="rounded-lg border border-dashed border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 px-4 py-3 text-xs text-[hsl(var(--ds-muted-fg))]">
              Use <strong className="text-[hsl(var(--ds-fg))]">+ New rate</strong> or{" "}
              <strong className="text-[hsl(var(--ds-fg))]">Edit</strong> on a row to open the rate form in a separate dialog.
              All fields for this act are available there; Save writes to the list and DB.
            </p>
          )}

      {/* New / Edit rate — modal editor for the focused act (header + footer own Save/Cancel) */}
      {rateForm &&
        createPortal(
          <ActionFormOverlay onClose={closeRateForm}>
            <ActionFormShell className="!w-[min(100%,920px)]">
              <ActionFormHeader
                title={`${rateForm === 'new' ? 'New' : 'Edit'} ${actTitle}`}
                subtitle={`Effective ${formatDate(effectiveDates[actSection] || effectiveDates.incomeTax)}`}
                onClose={closeRateForm}
              />
              <ActionFormBody>
      <div className="space-y-6">
        {/* PF Card */}
        {activeAct === 'pf' && (
        <ProvidentFundRatesSection
          embedded
          rates={rates}
          effectiveDate={effectiveDates.pf}
          onEffectiveDateChange={(value) => handleEffectiveDateChange('pf', value)}
          onRateChange={(field, value) => handleRateChange('pf', field, value)}
          minEffectiveDate={statutoryMinDate}
        />
        )}

        {/* Gratuity Card */}
        {activeAct === 'gratuity' && (
        <div>
          {/* Effective Date */}
          <div className="mb-4 p-3 bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] rounded">
            <label className="block text-sm font-medium text-[hsl(var(--ds-fg))] mb-1">
              Effective Date *
            </label>
            <DatePicker
              label="Effective Date"
              required
              value={effectiveDates.gratuity}
              onChange={(e) => handleEffectiveDateChange('gratuity', e.target.value)}
              min={statutoryMinDate}
              className="px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
            />
          </div>

          <div>
            <div>
              <label className="block text-xs font-medium text-[hsl(var(--ds-fg))] mb-1">
                Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={rates.gratuity.rate}
                onChange={(e) => handleRateChange('gratuity', 'rate', e.target.value)}
                className="w-full px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
              <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
                Monthly provision % of gratuity wage (usually Basic). Default 4.81% ≈ 15÷26÷12.
              </p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[hsl(var(--ds-fg))] mb-1">
                  Minimum Service — Full-time (years)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={rates.gratuity.minServiceYearsFullTime ?? 5}
                  onChange={(e) => handleRateChange('gratuity', 'minServiceYearsFullTime', e.target.value)}
                  className="w-full px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[hsl(var(--ds-fg))] mb-1">
                  Minimum Service — Contract (years)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={rates.gratuity.minServiceYearsContract ?? 5}
                  onChange={(e) => handleRateChange('gratuity', 'minServiceYearsContract', e.target.value)}
                  className="w-full px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                />
              </div>
            </div>
            <p className="mt-3 rounded border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-2 text-xs text-[hsl(var(--ds-fg))]">
              Gratuity = wages ÷ 26 × 15 × years
            </p>
          </div>
        </div>
        )}

        {/* Old Regime Tax Card */}
        {activeAct === 'tax' && regime === 'old' && (
        <div>
            {/* Effective Date */}
            <div className="mb-4 p-3 bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] rounded">
              <label className="block text-sm font-medium text-[hsl(var(--ds-fg))] mb-1">
                Effective Date *
              </label>
              <DatePicker
                label="Effective Date"
                required
                value={effectiveDates.incomeTaxOld || effectiveDates.incomeTax}
                onChange={(e) => handleEffectiveDateChange('incomeTaxOld', e.target.value)}
                min={statutoryMinDate}
                className="px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-500"
              />
            </div>

            <div className="space-y-4">
              {/* Cess Rate */}
              <div>
                <label className="block text-xs font-medium text-[hsl(var(--ds-fg))] mb-1">
                  Education & Health Cess (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={rates.incomeTax.cess}
                  onChange={(e) => handleRateChange('incomeTax', 'cess', e.target.value)}
                  className="w-full px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-500"
                />
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">4% over tax payable (FY 2024-25)</p>
              </div>

              {/* Tax Slabs */}
              <div className="bg-[hsl(var(--ds-muted))] p-3 rounded border border-[hsl(var(--ds-border))]">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-[hsl(var(--ds-fg))]">Tax Slabs</h4>
                  <button
                    onClick={() => handleAddIncomeTaxSlab('old')}
                    className="px-2 py-1 text-xs bg-[hsl(var(--ds-muted))] text-white rounded hover:bg-[hsl(var(--ds-muted))]"
                  >
                    + Add Slab
                  </button>
                </div>
                <div className="space-y-2">
                  {rates.incomeTax.slabs.old.map((slab, index) => (
                    <div key={index} className="bg-[hsl(var(--ds-surface))] p-2 rounded border border-[hsl(var(--ds-border))]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-[hsl(var(--ds-fg))] font-medium">Slab {index + 1}</span>
                        {rates.incomeTax.slabs.old.length > 1 && (
                          <button
                            onClick={() => handleDeleteIncomeTaxSlab('old', index)}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >
                            ✕ Delete
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <label className="block text-[hsl(var(--ds-fg))] mb-1">Min Income (₹)</label>
                          <input
                            type="number"
                            value={slab.min}
                            onChange={(e) => handleIncomeTaxSlabChange('old', index, 'min', e.target.value)}
                            className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[hsl(var(--ds-fg))] mb-1">Max Income (₹)</label>
                          <input
                            type="number"
                            value={slab.max === Infinity ? '' : slab.max}
                            onChange={(e) => handleIncomeTaxSlabChange('old', index, 'max', e.target.value === '' ? Infinity : e.target.value)}
                            placeholder="Above"
                            className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[hsl(var(--ds-fg))] mb-1">Tax Rate (%)</label>
                          <input
                            type="number"
                            value={slab.rate}
                            onChange={(e) => handleIncomeTaxSlabChange('old', index, 'rate', e.target.value)}
                            className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Surcharge */}
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-yellow-800">Surcharge (Income above ₹50 lakh)</h4>
                  <button
                    onClick={() => handleAddSurchargeSlab('old')}
                    className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
                  >
                    + Add Slab
                  </button>
                </div>
                <div className="space-y-2">
                  {rates.incomeTax.surcharge.old.map((slab, index) => (
                    <div key={index} className="bg-[hsl(var(--ds-surface))] p-2 rounded border border-yellow-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-yellow-700 font-medium">Surcharge Slab {index + 1}</span>
                        {rates.incomeTax.surcharge.old.length > 1 && (
                          <button
                            onClick={() => handleDeleteSurchargeSlab('old', index)}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >
                            ✕ Delete
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <label className="block text-yellow-700 mb-1">Min Income (₹)</label>
                          <input
                            type="number"
                            value={slab.min}
                            onChange={(e) => handleSurchargeChange('old', index, 'min', e.target.value)}
                            className="w-full px-1 py-1 border border-yellow-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-yellow-700 mb-1">Max Income (₹)</label>
                          <input
                            type="number"
                            value={slab.max === Infinity ? '' : slab.max}
                            onChange={(e) => handleSurchargeChange('old', index, 'max', e.target.value === '' ? Infinity : e.target.value)}
                            placeholder="Above"
                            className="w-full px-1 py-1 border border-yellow-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-yellow-700 mb-1">Surcharge Rate (%)</label>
                          <input
                            type="number"
                            value={slab.rate}
                            onChange={(e) => handleSurchargeChange('old', index, 'rate', e.target.value)}
                            className="w-full px-1 py-1 border border-yellow-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-yellow-600 mt-2">
                  * Surcharge applies to income above ₹50 lakh, levied on basic tax payable
                </p>
              </div>
            </div>
          </div>
        )}

          {/* ESI Card */}
          {activeAct === 'esi' && (
          <div>
            {/* Effective Date */}
            <div className="mb-4 p-3 bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] rounded">
              <label className="block text-sm font-medium text-[hsl(var(--ds-fg))] mb-1">
                Effective Date *
              </label>
              <DatePicker
                label="Effective Date"
                required
                value={effectiveDates.esi}
                onChange={(e) => handleEffectiveDateChange('esi', e.target.value)}
                min={statutoryMinDate}
                className="px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
            </div>

            <div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[hsl(var(--ds-fg))] mb-1">
                    Employee Share (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={rates.esi.employeeShare}
                    onChange={(e) => handleRateChange('esi', 'employeeShare', e.target.value)}
                    className="w-full px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[hsl(var(--ds-fg))] mb-1">
                    Employer Share (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={rates.esi.employerShare}
                    onChange={(e) => handleRateChange('esi', 'employerShare', e.target.value)}
                    className="w-full px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[hsl(var(--ds-fg))] mb-1">
                    Ceiling (₹)
                  </label>
                  <input
                    type="number"
                    value={rates.esi.ceiling}
                    onChange={(e) => handleRateChange('esi', 'ceiling', e.target.value)}
                    className="w-full px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[hsl(var(--ds-fg))] mb-1">
                    Cutoff (₹)
                  </label>
                  <input
                    type="number"
                    value={rates.esi.cutoff}
                    onChange={(e) => handleRateChange('esi', 'cutoff', e.target.value)}
                    title="Eligibility when fixed gross ≤ this amount. Contribution uses min(ESI wage, Cutoff)."
                    className="w-full px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                  />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* PT Card */}
          {activeAct === 'pt' && (
          <div>
            {/* Effective Date */}
            <div className="mb-4 p-3 bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] rounded">
              <label className="block text-sm font-medium text-[hsl(var(--ds-fg))] mb-1">
                Effective Date *
              </label>
              <DatePicker
                label="Effective Date"
                required
                value={effectiveDates.pt}
                onChange={(e) => handleEffectiveDateChange('pt', e.target.value)}
                min={statutoryMinDate}
                className="px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
            </div>

            <div className="space-y-4">
              <p className="text-xs text-[hsl(var(--ds-muted-fg))] bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] rounded p-2">
                Payroll uses each employee&apos;s <strong>Branch → State</strong> to pick these slabs.
                The state dropdown below is only for editing rates (not a company-wide override for employees).
              </p>
              {/* State Selection */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-[hsl(var(--ds-fg))]">
                    Edit rates for state
                  </label>
                  <DsButton type="button" size="sm" variant="primary" onClick={() => setShowAddStateModal(true)}>
                      + Add State
                    </DsButton>
                </div>
                <select
                  value={rates.pt.currentState}
                  onChange={(e) => handlePTStateChange(e.target.value)}
                  className="w-full px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                >
                  {Object.keys(rates.pt.states).map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {(() => {
                const ptCfg = getPtConfigForState(rates, rates.pt.currentState);
                return (
                  <>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <label className="block text-[hsl(var(--ds-muted-fg))] mb-1">Frequency</label>
                        <select
                          value={ptCfg.frequency}
                          onChange={(e) => handlePtMetaChange('frequency', e.target.value)}
                          className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs"
                        >
                          <option value="MONTHLY">Monthly</option>
                          <option value="HALF_YEARLY">Half-yearly</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[hsl(var(--ds-muted-fg))] mb-1">Annual cap (₹)</label>
                        <input
                          type="number"
                          value={ptCfg.annualCap ?? ''}
                          onChange={(e) =>
                            handlePtMetaChange(
                              'annualCap',
                              e.target.value === '' ? null : Number(e.target.value)
                            )
                          }
                          placeholder="None"
                          className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs"
                        />
                      </div>
                      <div className="flex items-center gap-2 col-span-2">
                        <input
                          type="checkbox"
                          id="ptApplicableState"
                          checked={ptCfg.applicable !== false}
                          onChange={(e) => handlePtMetaChange('applicable', e.target.checked)}
                        />
                        <label htmlFor="ptApplicableState">State levies PT</label>
                      </div>
                      {ptCfg.frequency === 'HALF_YEARLY' && (
                        <div className="col-span-2">
                          <label className="block text-[hsl(var(--ds-muted-fg))] mb-1">
                            Deduct months (1–12, comma-separated)
                          </label>
                          <input
                            type="text"
                            value={(ptCfg.deductMonths || []).join(',')}
                            onChange={(e) =>
                              handlePtMetaChange(
                                'deductMonths',
                                e.target.value
                                  .split(',')
                                  .map((x) => Number(String(x).trim()))
                                  .filter((n) => n >= 1 && n <= 12)
                              )
                            }
                            placeholder="9,3"
                            className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs"
                          />
                        </div>
                      )}
                    </div>

                    {/* PT Slabs */}
                    <div className="bg-[hsl(var(--ds-muted))] p-3 rounded border border-[hsl(var(--ds-border))]">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-[hsl(var(--ds-fg))]">
                          Professional Tax Slabs ({rates.pt.currentState})
                          {ptCfg.genderSlabs ? ' — default / male path' : ''}
                        </h4>
                        <DsButton type="button" size="sm" variant="primary" onClick={handleAddPTSlab}>
                            + Add Slab
                          </DsButton>
                      </div>
                      <div className="space-y-2">
                        {(getPtSlabsForUi(rates, rates.pt.currentState)).map((slab, index) => (
                          <div key={index} className="bg-[hsl(var(--ds-surface))] p-2 rounded border border-[hsl(var(--ds-border))]">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-[hsl(var(--ds-muted-fg))] font-medium">Slab {index + 1}</span>
                              {getPtSlabsForUi(rates, rates.pt.currentState).length > 1 && (
                                <button
                                  onClick={() => handleDeletePTSlab(index)}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  ✕ Delete
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div>
                                <label className="block text-[hsl(var(--ds-muted-fg))] mb-1">Min Salary (₹)</label>
                                <input
                                  type="number"
                                  value={slab.min}
                                  onChange={(e) => handlePTRateChange(index, 'min', e.target.value)}
                                  className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                                />
                              </div>
                              <div>
                                <label className="block text-[hsl(var(--ds-muted-fg))] mb-1">Max Salary (₹)</label>
                                <input
                                  type="number"
                                  value={slab.max === Infinity ? '' : slab.max}
                                  onChange={(e) => handlePTRateChange(index, 'max', e.target.value === '' ? Infinity : e.target.value)}
                                  placeholder="Above"
                                  className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                                />
                              </div>
                              <div>
                                <label className="block text-[hsl(var(--ds-muted-fg))] mb-1">Tax Amount (₹)</label>
                                <input
                                  type="number"
                                  value={slab.rate}
                                  onChange={(e) => handlePTRateChange(index, 'rate', e.target.value)}
                                  className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-2">
                        * These slabs apply every month unless a Month override below changes the tax for a specific month.
                      </p>
                    </div>

                    {/* Month overrides — Karnataka / Maharashtra February ₹300 pattern */}
                    <div className="bg-amber-50 p-3 rounded border border-amber-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-amber-900">
                          Month overrides (e.g. February ₹300)
                        </h4>
                        <DsButton type="button" size="sm" variant="secondary" onClick={handleAddPtMonthOverride}>
                          + Add override
                        </DsButton>
                      </div>
                      <p className="text-[11px] text-amber-800 mb-2">
                        Example — Karnataka: slabs say ₹200 all year; add February override with Min salary ₹25,000 and Tax ₹300.
                        Maharashtra can use Gender (Male / Female) on the same pattern.
                      </p>
                      <div className="space-y-2">
                        {(ptCfg.specialRules || []).map((rule, index) => {
                          if (rule?.type && String(rule.type).toUpperCase() !== 'MONTH_OVERRIDE') {
                            return null;
                          }
                          return (
                          <div key={index} className="bg-[hsl(var(--ds-surface))] p-2 rounded border border-amber-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-amber-800 font-medium">Override {index + 1}</span>
                              <button
                                type="button"
                                onClick={() => handleDeletePtMonthOverride(index)}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                ✕ Delete
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                              <div>
                                <label className="block text-amber-800 mb-1">Month</label>
                                <select
                                  value={Number(rule.month) || 2}
                                  onChange={(e) => handlePtMonthOverrideChange(index, 'month', e.target.value)}
                                  className="w-full px-1 py-1 border border-amber-300 rounded text-xs"
                                >
                                  {MONTH_OPTIONS.map((m) => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-amber-800 mb-1">Gender (optional)</label>
                                <select
                                  value={rule.gender || ''}
                                  onChange={(e) => handlePtMonthOverrideChange(index, 'gender', e.target.value)}
                                  className="w-full px-1 py-1 border border-amber-300 rounded text-xs"
                                >
                                  <option value="">All</option>
                                  <option value="male">Male</option>
                                  <option value="female">Female</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-amber-800 mb-1">Min salary (₹)</label>
                                <input
                                  type="number"
                                  value={rule.minGross ?? 0}
                                  onChange={(e) => handlePtMonthOverrideChange(index, 'minGross', e.target.value)}
                                  className="w-full px-1 py-1 border border-amber-300 rounded text-xs"
                                />
                              </div>
                              <div>
                                <label className="block text-amber-800 mb-1">Tax amount (₹)</label>
                                <input
                                  type="number"
                                  value={rule.replaceAmount ?? ''}
                                  onChange={(e) => handlePtMonthOverrideChange(index, 'replaceAmount', e.target.value)}
                                  className="w-full px-1 py-1 border border-amber-300 rounded text-xs"
                                />
                              </div>
                            </div>
                          </div>
                          );
                        })}
                        {!(ptCfg.specialRules || []).length && (
                          <p className="text-xs text-amber-700 italic">
                            No month overrides yet. Click + Add override for February ₹300 (typical for Karnataka / Maharashtra).
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
          )}

          {/* New Regime Tax Card */}
          {activeAct === 'tax' && regime === 'new' && (
          <div>
            {/* Effective Date */}
            <div className="mb-4 p-3 bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] rounded">
              <label className="block text-sm font-medium text-[hsl(var(--ds-fg))] mb-1">
                Effective Date *
              </label>
              <DatePicker
                label="Effective Date"
                required
                value={effectiveDates.incomeTaxNew || effectiveDates.incomeTax}
                onChange={(e) => handleEffectiveDateChange('incomeTaxNew', e.target.value)}
                min={statutoryMinDate}
                className="px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
            </div>

            <div className="space-y-4">
              {/* Cess Rate */}
              <div>
                <label className="block text-xs font-medium text-[hsl(var(--ds-fg))] mb-1">
                  Education & Health Cess (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={rates.incomeTax.cess}
                  onChange={(e) => handleRateChange('incomeTax', 'cess', e.target.value)}
                  className="w-full px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                />
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">4% over tax payable (FY 2024-25)</p>
              </div>

              {/* Tax Slabs */}
              <div className="bg-[hsl(var(--ds-muted))] p-3 rounded border border-[hsl(var(--ds-border))]">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-[hsl(var(--ds-fg))]">Tax Slabs</h4>
                  <button
                    onClick={() => handleAddIncomeTaxSlab('new')}
                    className="px-2 py-1 text-xs bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] rounded hover:bg-[hsl(var(--ds-primary-hover))]"
                  >
                    + Add Slab
                  </button>
                </div>
                <div className="space-y-2">
                  {rates.incomeTax.slabs.new.map((slab, index) => (
                    <div key={index} className="bg-[hsl(var(--ds-surface))] p-2 rounded border border-[hsl(var(--ds-border))]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-green-700 font-medium">Slab {index + 1}</span>
                        {rates.incomeTax.slabs.new.length > 1 && (
                          <button
                            onClick={() => handleDeleteIncomeTaxSlab('new', index)}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >
                            ✕ Delete
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <label className="block text-green-700 mb-1">Min Income (₹)</label>
                          <input
                            type="number"
                            value={slab.min}
                            onChange={(e) => handleIncomeTaxSlabChange('new', index, 'min', e.target.value)}
                            className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                          />
                        </div>
                        <div>
                          <label className="block text-green-700 mb-1">Max Income (₹)</label>
                          <input
                            type="number"
                            value={slab.max === Infinity ? '' : slab.max}
                            onChange={(e) => handleIncomeTaxSlabChange('new', index, 'max', e.target.value === '' ? Infinity : e.target.value)}
                            placeholder="Above"
                            className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                          />
                        </div>
                        <div>
                          <label className="block text-green-700 mb-1">Tax Rate (%)</label>
                          <input
                            type="number"
                            value={slab.rate}
                            onChange={(e) => handleIncomeTaxSlabChange('new', index, 'rate', e.target.value)}
                            className="w-full px-1 py-1 border border-[hsl(var(--ds-border))] rounded text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Surcharge */}
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-yellow-800">Surcharge (Income above ₹50 lakh)</h4>
                  <button
                    onClick={() => handleAddSurchargeSlab('new')}
                    className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
                  >
                    + Add Slab
                  </button>
                </div>
                <div className="space-y-2">
                  {rates.incomeTax.surcharge.new.map((slab, index) => (
                    <div key={index} className="bg-[hsl(var(--ds-surface))] p-2 rounded border border-yellow-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-yellow-700 font-medium">Surcharge Slab {index + 1}</span>
                        {rates.incomeTax.surcharge.new.length > 1 && (
                          <button
                            onClick={() => handleDeleteSurchargeSlab('new', index)}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >
                            ✕ Delete
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <label className="block text-yellow-700 mb-1">Min Income (₹)</label>
                          <input
                            type="number"
                            value={slab.min}
                            onChange={(e) => handleSurchargeChange('new', index, 'min', e.target.value)}
                            className="w-full px-1 py-1 border border-yellow-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-yellow-700 mb-1">Max Income (₹)</label>
                          <input
                            type="number"
                            value={slab.max === Infinity ? '' : slab.max}
                            onChange={(e) => handleSurchargeChange('new', index, 'max', e.target.value === '' ? Infinity : e.target.value)}
                            placeholder="Above"
                            className="w-full px-1 py-1 border border-yellow-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-yellow-700 mb-1">Surcharge Rate (%)</label>
                          <input
                            type="number"
                            value={slab.rate}
                            onChange={(e) => handleSurchargeChange('new', index, 'rate', e.target.value)}
                            className="w-full px-1 py-1 border border-yellow-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-yellow-600 mt-2">
                  * Surcharge applies to income above ₹50 lakh, levied on basic tax payable
                </p>
              </div>
            </div>
          </div>
          )}
      </div>
              </ActionFormBody>
              <ActionFormFooter>
                <DsButton type="button" size="sm" variant="secondary" onClick={closeRateForm}>
                  Cancel
                </DsButton>
                <DsButton
                  type="button"
                  size="sm"
                  variant="primary"
                  disabled={savingConfig}
                  onClick={async () => {
                    if (await handleSaveSection(actSection)) {
                      rateSnapRef.current = null;
                      setRateForm(null);
                    }
                  }}
                >
                  Save
                </DsButton>
              </ActionFormFooter>
            </ActionFormShell>
          </ActionFormOverlay>,
          document.body
        )}
        </div>
      </div>

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[hsl(var(--ds-surface))] rounded-lg shadow-xl w-[90vw] max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--ds-border))]">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-[hsl(var(--ds-fg))]">
                  {selectedSection === 'pt' ? 'Professional Tax Rate History' : `${selectedSection.toUpperCase()} Rate History`}
                </h2>
                {selectedSection === 'pt' && (
                  <select
                    value={ptHistoryState}
                    onChange={(e) => setPtHistoryState(e.target.value)}
                    className="px-3 py-1 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  >
                    {Object.keys(rates.pt.states).map(state => (
                                            <option key={state?.id ?? state} value={state?.id ?? state}>
                        {state?.name ?? state}
                      </option>

                    ))}
                  </select>
                )}
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-muted-fg))]"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                {(() => {
                  const entries = getHistoryEntriesForModal();
                  const latestDate = entries[0]
                    ? String(entries[0].date).slice(0, 10)
                    : null;
                  return entries.map((entry) => {
                    const entryDate = String(entry.date).slice(0, 10);
                    const isLatest = entryDate === latestDate;
                    const canDelete = isLatest && entries.length > 1;
                    return (
                  <div key={historyEntryKey(entry)} className="border border-[hsl(var(--ds-border))] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3 gap-3">
                      <div>
                        <h3 className="text-lg font-medium text-[hsl(var(--ds-fg))]">
                          Effective Date: {formatDate(entry.date)}
                        </h3>
                        <p className="text-sm text-[hsl(var(--ds-muted-fg))]">{entry.changes}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isLatest ? 'bg-green-100 text-[hsl(var(--ds-fg))]' : 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]'
                        }`}>
                          {isLatest ? 'Latest' : 'Historical'}
                        </span>
                        {canDelete ? (
                          <DsButton
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-[hsl(var(--ds-danger))] hover:text-[hsl(var(--ds-danger))]"
                            disabled={savingConfig}
                            onClick={() => handleDeleteHistoryEntry(entry)}
                            title="Delete this entry and revert live rates to the previous update"
                          >
                            <Trash2 size={14} className="mr-1" />
                            Delete
                          </DsButton>
                        ) : (
                          <span
                            className="max-w-[150px] text-right text-[10px] text-[hsl(var(--ds-muted-fg))]"
                            title={
                              entries.length <= 1
                                ? "Cannot delete the only history entry"
                                : "Only the latest entry can be deleted (remove newest first)"
                            }
                          >
                            {entries.length <= 1
                              ? "Delete unavailable"
                              : "Delete newest first"}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="bg-[hsl(var(--ds-muted))] p-3 rounded">
                      {selectedSection === 'pf' && (
                        <div className="space-y-2 text-xs">
                          <div className="grid grid-cols-2 gap-2">
                            <div><strong>Employee Share:</strong> {entry.rates.employeeShare}%</div>
                            <div><strong>Employer Share:</strong> {entry.rates.employerShare}%</div>
                            <div><strong>Ceiling:</strong> ₹{(entry.rates.pfWageCeiling ?? entry.rates.ceiling)?.toLocaleString?.() ?? entry.rates.ceiling}</div>
                            <div>
                              <strong>Prorate Ceiling:</strong>{" "}
                              {entry.rates.prorateCeiling
                                ? "Yes — ceiling ÷ calendar days × payable days"
                                : "No — full monthly ceiling"}
                            </div>
                            <div><strong>EPF Rate:</strong> {entry.rates.epf}%</div>
                            <div><strong>EPS Rate:</strong> {entry.rates.eps}%</div>
                            <div><strong>Admin Charges:</strong> {entry.rates.adminCharges}%</div>
                            <div><strong>EDLI Rate:</strong> {entry.rates.edliRate}%</div>
                          </div>
                        </div>
                      )}
                      {selectedSection === 'esi' && (
                        <div className="space-y-2 text-xs">
                          <div className="grid grid-cols-2 gap-2">
                            <div><strong>Employee Share:</strong> {entry.rates.employeeShare}%</div>
                            <div><strong>Employer Share:</strong> {entry.rates.employerShare}%</div>
                            <div><strong>Ceiling:</strong> ₹{entry.rates.ceiling?.toLocaleString()}</div>
                            <div><strong>Cutoff:</strong> ₹{entry.rates.cutoff?.toLocaleString()}</div>
                          </div>
                        </div>
                      )}
                      {selectedSection === 'pt' && (
                        <div className="space-y-2 text-xs">
                          <div><strong>State:</strong> {ptHistoryState}</div>
                          {entry.rates?.frequency && (
                            <div><strong>Frequency:</strong> {entry.rates.frequency}</div>
                          )}
                          {entry.rates?.annualCap != null && (
                            <div><strong>Annual cap:</strong> ₹{Number(entry.rates.annualCap).toLocaleString()}</div>
                          )}
                          <div><strong>Slabs:</strong></div>
                          <div className="ml-4 space-y-1">
                            {(entry.rates.slabs || (Array.isArray(entry.rates) ? entry.rates : []))?.map((slab, idx) => (
                              <div key={idx} className="text-[hsl(var(--ds-muted-fg))]">
                                ₹{slab.min?.toLocaleString()} - {slab.max === Infinity || slab.max == null ? 'Above' : `₹${slab.max?.toLocaleString()}`}: ₹{slab.rate}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedSection === 'gratuity' && (
                        <div className="space-y-2 text-xs">
                          <div><strong>Rate:</strong> {entry.rates.rate}%</div>
                          <div className="text-[hsl(var(--ds-muted-fg))]">Percentage of basic salary per year of service</div>
                        </div>
                      )}
                      {selectedSection === 'incomeTax' && (
                        <div className="space-y-2 text-xs">
                          <div><strong>Education & Health Cess:</strong> {entry.rates.cess}%</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <div className="font-medium text-[hsl(var(--ds-fg))] mb-1">Old Regime Tax Slabs:</div>
                              <div className="space-y-1">
                                {entry.rates.slabs?.old?.map((slab, idx) => (
                                  <div key={idx} className="text-[hsl(var(--ds-muted-fg))]">
                                    ₹{slab.min?.toLocaleString()} - {slab.max === Infinity ? 'Above' : `₹${slab.max?.toLocaleString()}`}: {slab.rate}%
                                  </div>
                                ))}
                              </div>
                              <div className="font-medium text-[hsl(var(--ds-fg))] mb-1 mt-2">Old Regime Surcharge:</div>
                              <div className="space-y-1">
                                {entry.rates.surcharge?.old?.map((slab, idx) => (
                                  <div key={idx} className="text-[hsl(var(--ds-muted-fg))]">
                                    ₹{slab.min?.toLocaleString()} - {slab.max === Infinity ? 'Above' : `₹${slab.max?.toLocaleString()}`}: {slab.rate}%
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <div className="font-medium text-[hsl(var(--ds-fg))] mb-1">New Regime Tax Slabs:</div>
                              <div className="space-y-1">
                                {entry.rates.slabs?.new?.map((slab, idx) => (
                                  <div key={idx} className="text-[hsl(var(--ds-muted-fg))]">
                                    ₹{slab.min?.toLocaleString()} - {slab.max === Infinity ? 'Above' : `₹${slab.max?.toLocaleString()}`}: {slab.rate}%
                                  </div>
                                ))}
                              </div>
                              <div className="font-medium text-[hsl(var(--ds-fg))] mb-1 mt-2">New Regime Surcharge:</div>
                              <div className="space-y-1">
                                {entry.rates.surcharge?.new?.map((slab, idx) => (
                                  <div key={idx} className="text-[hsl(var(--ds-muted-fg))]">
                                    ₹{slab.min?.toLocaleString()} - {slab.max === Infinity ? 'Above' : `₹${slab.max?.toLocaleString()}`}: {slab.rate}%
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedSection === 'incomeTaxOld' && (
                        <div className="space-y-2 text-xs">
                          <div><strong>Education & Health Cess:</strong> {entry.rates.cess}%</div>
                          <div className="font-medium text-[hsl(var(--ds-fg))] mb-1">Tax Slabs:</div>
                          <div className="space-y-1">
                            {entry.rates.slabs?.map((slab, idx) => (
                              <div key={idx} className="text-[hsl(var(--ds-muted-fg))]">
                                ₹{slab.min?.toLocaleString()} - {slab.max === Infinity ? 'Above' : `₹${slab.max?.toLocaleString()}`}: {slab.rate}%
                              </div>
                            ))}
                          </div>
                          <div className="font-medium text-[hsl(var(--ds-fg))] mb-1 mt-2">Surcharge:</div>
                          <div className="space-y-1">
                            {entry.rates.surcharge?.map((slab, idx) => (
                              <div key={idx} className="text-[hsl(var(--ds-muted-fg))]">
                                ₹{slab.min?.toLocaleString()} - {slab.max === Infinity ? 'Above' : `₹${slab.max?.toLocaleString()}`}: {slab.rate}%
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedSection === 'incomeTaxNew' && (
                        <div className="space-y-2 text-xs">
                          <div><strong>Education & Health Cess:</strong> {entry.rates.cess}%</div>
                          <div className="font-medium text-[hsl(var(--ds-fg))] mb-1">Tax Slabs:</div>
                          <div className="space-y-1">
                            {entry.rates.slabs?.map((slab, idx) => (
                              <div key={idx} className="text-[hsl(var(--ds-muted-fg))]">
                                ₹{slab.min?.toLocaleString()} - {slab.max === Infinity ? 'Above' : `₹${slab.max?.toLocaleString()}`}: {slab.rate}%
                              </div>
                            ))}
                          </div>
                          <div className="font-medium text-[hsl(var(--ds-fg))] mb-1 mt-2">Surcharge:</div>
                          <div className="space-y-1">
                            {entry.rates.surcharge?.map((slab, idx) => (
                              <div key={idx} className="text-[hsl(var(--ds-muted-fg))]">
                                ₹{slab.min?.toLocaleString()} - {slab.max === Infinity ? 'Above' : `₹${slab.max?.toLocaleString()}`}: {slab.rate}%
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                    );
                  });
                })()}
              </div>
              
              {getHistoryEntriesForModal().length === 0 && (
                <div className="text-center py-8">
                  <p className="text-[hsl(var(--ds-muted-fg))]">No rate history available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add New State Modal */}
      {showAddStateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10030]">
          <div className="bg-[hsl(var(--ds-surface))] rounded-lg shadow-xl w-96 max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--ds-border))]">
              <h2 className="text-xl font-semibold text-[hsl(var(--ds-fg))]">Add New State</h2>
              <button
                onClick={() => setShowAddStateModal(false)}
                className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-muted-fg))]"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-[hsl(var(--ds-fg))] mb-2">
                  State Name
                </label>
                <DsInput
                  type="text"
                  value={newStateName}
                  onChange={(e) => setNewStateName(e.target.value)}
                  placeholder="Enter state name (e.g., Kerala)"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddNewState()}
                  />
              </div>
              
              <div className="flex justify-end space-x-3">
                <DsButton type="button" size="sm" variant="secondary" onClick={() => setShowAddStateModal(false)}>
                    Cancel
                  </DsButton>
                <DsButton type="button" size="sm" variant="primary" onClick={handleAddNewState}>
                  Add State
                </DsButton>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StatutoryRatesManager;
